import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import {
  AdminListCard,
  AdminListTable,
  ConfirmDeleteModal,
  PageTabs,
  saveErrorReason,
  useBatchSelection,
  withErrorReason,
  withFailureReasons,
} from "@components/CatalogIndex";
import type { BatchFailure } from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useLanguageFieldsList, useLanguageFieldMutations } from "../api";
import {
  buildLanguageFieldBatchActions,
  type LanguageFieldTab,
} from "./batchActions";
import type { LanguageField } from "../types";

const List = (): JSX.Element => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<LanguageFieldTab>("active");

  const sel = useBatchSelection([tab]);
  const { archive, restore, move } = useLanguageFieldMutations();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);

  // Language fields are drag-reorderable by position, so load the full set
  // (no pagination — global position ordering and paging don't mix).
  const { rows, pageMeta, isLoading, isFetching, refetch } =
    useLanguageFieldsList({
      page: { size: 500 },
      filter: { active: tab === "active" },
    });

  const selected = useMemo(
    () => rows.filter((r) => sel.selectedIds.includes(r.id)),
    [rows, sel.selectedIds]
  );

  const columns: ColumnDef<LanguageField>[] = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => row.original.name,
    },
    {
      accessorKey: "sowLanguage",
      header: "Service Description",
      cell: ({ row }) => row.original.sowLanguage,
    },
  ];

  const runOnSelected = async (
    fn: (l: LanguageField) => Promise<unknown>,
    successText: (n: number) => string
  ) => {
    const targets = [...selected];
    const failures: BatchFailure[] = [];
    for (const l of targets) {
      try {
        await fn(l);
      } catch (err) {
        failures.push({ label: l.name, reason: saveErrorReason(err) });
      }
    }
    setToast(
      failures.length === 0
        ? { variant: "success", text: successText(targets.length) }
        : {
            variant: "danger",
            text: withFailureReasons(
              `Failed on ${failures.length} of ${targets.length} language field(s).`,
              failures
            ),
          }
    );
    sel.clear();
    refetch();
  };

  const handleConfirm = async () => {
    setConfirmOpen(false);
    if (tab === "active") {
      await runOnSelected(
        (l) => archive(l.id),
        (n) => `${n} language field(s) archived.`
      );
    } else {
      await runOnSelected(
        (l) => restore(l.id, l.name),
        (n) => `${n} language field(s) restored.`
      );
    }
  };

  const handleReorder = async (
    moves: Array<{ id: string | number; position: number }>
  ) => {
    try {
      for (const { id, position } of moves) {
        await move(String(id), position);
      }
      refetch();
    } catch (err) {
      setToast({
        variant: "danger",
        text: withErrorReason(
          "Could not reorder. The list has been refreshed.",
          err
        ),
      });
      refetch();
    }
  };

  const batchActions = buildLanguageFieldBatchActions(
    tab,
    sel.selectedIds.length,
    {
      onDelete: () => setConfirmOpen(true),
      onRestore: () => setConfirmOpen(true),
    }
  );

  if (isLoading) return <ScopeStackSpinner />;

  return (
    <>
      <AdminListCard
        title="Language Fields"
        onNew={() => navigate("new")}
        newButtonLabel="+ Add A Language Field"
        batchActions={batchActions}
        selectedCount={sel.selectedIds.length}
      >
        <PageTabs
          activeKey={tab}
          onSelect={(key) => {
            setTab(key as LanguageFieldTab);
            sel.clear();
          }}
          tabs={[
            {
              key: "active",
              label: "Active",
              count: tab === "active" ? pageMeta.recordCount : undefined,
            },
            {
              key: "archived",
              label: "Archived",
              count: tab === "archived" ? pageMeta.recordCount : undefined,
            },
          ]}
        />
        <AdminListTable
          columns={columns}
          data={rows}
          isLoading={isFetching}
          selectedIds={sel.selectedIds}
          setSelectedIds={sel.setSelectedIds}
          draggable={tab === "active"}
          onReorder={tab === "active" ? handleReorder : undefined}
          onEdit={(id) => navigate(`${id}/edit`)}
          emptyMessage="No language fields found."
        />
      </AdminListCard>

      <ConfirmDeleteModal
        open={confirmOpen}
        title={
          tab === "active"
            ? "Archive language fields?"
            : "Restore language fields?"
        }
        message={`Are you sure you want to ${
          tab === "active" ? "archive" : "restore"
        } ${sel.selectedIds.length} language field(s)?`}
        confirmLabel={tab === "active" ? "Archive" : "Restore"}
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
      />

      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default List;
